import { ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, FormGroup } from "@angular/forms";
import { Period } from "src/app/ui/models/domains/pbm/period";
import { PageableResult } from "src/app/ui/models/domains/paginator/pageable-result";
import { MaskInput } from "src/app/ui/models/domains/masks/mask.model";
import { LoadingService } from "src/app/ui/services/loading.service";
import { CompanyService } from "src/app/ui/services/pbm/company.service";
import { Company } from "src/app/ui/models/domains/pbm/companies/company";
import { Toast } from "../../../toast/toast";
import { Beneficiary } from "src/app/ui/models/domains/pbm/Beneficiary/beneficiary";
import { BeneficiaryService } from "src/app/ui/services/pbm/beneficiary.service";
import { BenefitService } from "src/app/ui/services/pbm/benefit.service";
import { Dependent } from "src/app/ui/models/domains/pbm/Beneficiary/dependent";
import { FinancialStatementService } from "src/app/ui/services/pbm/financial-statement.service";
import { FinancialBalance } from "src/app/ui/models/domains/pbm/financial-satatement/financial-balance";
import { PagingRequest } from "src/app/ui/models/domains/paginator/paging-request";
import { LifeCycleConfiguration } from "src/app/ui/models/domains/pbm/Benefit/life-cycle-configuration";
import { SearchNetworkPayload } from "../../network-term/models/search-network.model";
import { NetworkDto } from "src/app/ui/repositories/pbm/network.repository";
import { NetworkService } from "../../network-term/services/network.service";
import { SalesOrderResult } from "src/app/ui/models/domains/pbm/financial-satatement/sales-order-result";
import { FinancialStatementViewItem } from "./view-items/financial-statement-view-item";
import { FinancialStatementHeaderViewItemFactory } from "./factory/financial-statement-header-view-item.factory";
import { FinancialStatementSearchValidator } from "../financial-statement-search.util";

class SelectedBeneficiaryView {
  title!: string;
  name!: string;
  registration!: string | null;
  region!: string | null;
  socialNumber!: string;
  cardNumber!: string | null;
  limit: number | null = 0;
  dueDate: string | null = null;
  availableBalance: number | null = 0;
  dependents: number | null = null;
  status!: boolean;
  color: string | null = null;
  backgroundColor: string | null = null;
  selected: boolean = false;
  totalSpent: number | null = 0
  limitType: number | null
  isLoading?: boolean = false;
  constructor(init?: Partial<SelectedBeneficiaryView>) {
    Object.assign(this, init);
  }
}

@Component({
  selector: "financial-statement-beneficiary",
  templateUrl: "./financial-statement-beneficiary.component.html",
  styleUrls: ["./financial-statement-beneficiary.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class FinancialStatementBeneficiaryComponent implements OnInit {
  company: Company;
  companyLifeCycle: LifeCycleConfiguration;
  beneficiaryList: PageableResult;
  beneficiary: Beneficiary;
  beneficiaryLimitType: number;
  dependents: Array<Dependent>;
  dates: Array<Period>;
  fiscalNumber: string;
  selectedBeneficiary: Beneficiary;
  financialStatementItems: FinancialStatementViewItem[];
  socialNumberMask = MaskInput.getSocialNumberMask();
  cardNumberMask = MaskInput.getCardNumberMask();
  totalBalance: number;
  searchInput: any;
  rootForm: FormGroup = new FormGroup({
    months: new FormControl([]),
    networkFilter: new FormControl(""),
  });
  selectedBeneficiaries: SelectedBeneficiaryView[] = [];
  dataSource: any[] = [];
  financialStatementDataSource: any[] = [];
  displayedColumns: string[] = ["select", "title", "name", "registration", "socialNumber", "cardNumber", "limit", "dependents", "status"];
  displayedResumeColumns: string[] = ["date", "network", "beneficiary", "product", "quantity", "value", "discount", "subsidy", "deduction", "paid"];
  private processedDependents = new Set<string>();
  isLimitLoading = false;
  loadingSocialNumber: string | null = null;
  isLoadingNetwork = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private toast: Toast,
    private loadingService: LoadingService,
    private companyService: CompanyService,
    private beneficiaryService: BeneficiaryService,
    private benefitService: BenefitService,
    private financialStatementService: FinancialStatementService,
    private networkService: NetworkService,
    private cdk: ChangeDetectorRef
  ) {
    this.beneficiaryList = new PageableResult();
  }

  ngOnInit() {
    const paramns = this.activatedRoute.snapshot.queryParams;
    this.fiscalNumber = paramns.f;
    this.searchInput = {
      type: Number(paramns.dep),
      searchType: Number(paramns.searchType),
      search: paramns.ft
    };
    this.searchCompany();
    this.searchBeneficiaryInfo(this.searchInput.type, this.searchInput.searchType, this.searchInput.search);
  }

  async searchCompany() {
    this.company = null;
    this.companyLifeCycle = null;
    this.cleanFinancialStatement();
    const ref = this.loadingService.beginLoading();
    try {
      this.company = await this.companyService.findCompanyBy(this.fiscalNumber);
      this.companyLifeCycle = await this.benefitService.findLifeCycleByFiscalNumber(this.fiscalNumber);
      this.definePeriodList();
    } catch (error) {
      this.toast.showWarningToast(error);
    } finally {
      this.loadingService.finishLoading(ref);
    }
  }

  //#region Search Beneficiary
  onSearch(event) {
    this.searchBeneficiaryInfo(event.type, event.searchType, event.search);
  }

  getResultQuantityPhrase(): string {
    if (!this.beneficiaryList || this.beneficiaryList.getItems().length === 0) {
      return "Nenhum resultado encontrado.";
    }
    return `${this.beneficiaryList.getItems().length} RESULTADO(S) ENCONTRADO(S)`;
  }

  getBeneficiaries(): Beneficiary[] {
    return this.beneficiaryList.getItems() as Beneficiary[];
  }

  async searchBeneficiaryInfo(type: number, searchType: number, filterText: string) {
    this.cleanFinancialStatement();

    const searchValue = filterText?.toString() ?? "";
    if (!searchValue.trim()) {
      return;
    }

    const validation = FinancialStatementSearchValidator.validate(searchType, searchValue);
    if (!validation.isValid) {
      if (validation.errorMessage) {
        this.toast.showWarningToast(validation.errorMessage);
      }
      return;
    }

    const sanitizedFilter = validation.sanitizedValue;
    const ref = this.loadingService.beginLoading();
    try {
      const pagingRequest = new PagingRequest();
      pagingRequest.setStart(0);
      pagingRequest.setEnd(10);
      pagingRequest.setFilterText(sanitizedFilter);
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { f: this.fiscalNumber, s: null, ft: sanitizedFilter, dep: type, searchType: searchType },
      });

      this.beneficiaryList = await this.beneficiaryService.findBeneficiariesByFiscalNumber(
        this.fiscalNumber,
        pagingRequest,
        type === 0 ? null : type === 1 ? false : true
      );
    } catch (errorMessage) {
      this.toast.showWarningToast(errorMessage);
    } finally {
      this.loadingService.finishLoading(ref);
    }
  }

  goToBeneficiaryInfo(beneficiary: Beneficiary): void {

    this.selectedBeneficiary = beneficiary;
    this.getBeneficiaryInfo();
  }

  private async getBeneficiaryInfo(): Promise<void> {
    const ref = this.loadingService.beginLoading();
    try {
      const key = this.selectedBeneficiary.getParentBeneficiaryKey() ? this.selectedBeneficiary.getParentBeneficiaryKey() : this.selectedBeneficiary.getBeneficiaryKey();

      this.beneficiary = await this.beneficiaryService.findBeneficiaryByKey(key);
      this.beneficiary.setLimitType(this.selectedBeneficiary.getLimitType())
      this.dependents = this.beneficiary.getDependets().map((d) => {
        d.setLimitType(this.selectedBeneficiary.getLimitType())
        return d
      });
      this.fetchDataSource();
      this.onFilterChange();
      this.beneficiaryList = new PageableResult();
      this.cdk.detectChanges();
    } catch (errorMessage) {
      console.error(errorMessage);
    } finally {
      this.loadingService.finishLoading(ref);
      if (!this.beneficiary) {
        this.toast.showWarningToast("Nehum beneficiário encontrado para o filtro selecionado");
      }
    }
  }

  //#endregion

  //#region Network Filter
  networkOptions: NetworkDto[] = [];
  selectedNetwork: NetworkDto | null = null;

  find = async (value: string, page: number) => {
    const filterText = value.replace(/[./-]/g, "");
    if (typeof (value) != typeof ('') || value.length < 1 || this.isLoadingNetwork) {
      return [];
    }

    try {
      this.isLoadingNetwork = true;
      const request: SearchNetworkPayload = {
        Select: 'fiscalNumber,headOffice',
        Expand: 'headOffice($expand=term;$select=tradingName,corporateName,fiscalNumber)',
        Count: true,
        Top: 10,
        Skip: (page - 1) * 10,
      };
      const sanitizedSearchText = filterText.replace(/[./-]/g, '');
      if (sanitizedSearchText.length > 2) {
        // Busca parcial apenas nos nomes
        request.Filter = `(fiscalNumber eq '${sanitizedSearchText}' or headOffice/fiscalNumber eq '${sanitizedSearchText}') or (indexof(headOffice/corporateName, '${sanitizedSearchText}') gt -1 or indexof(headOffice/tradingName, '${sanitizedSearchText}') gt -1)`;
      } else {
        // Busca ampla
        request.Filter = '';
      }
      await new Promise<void>((resolve, reject) => {
        this.searchNetworkTermByFiscalNumber(request)
          .subscribe({
            next: response => {
              this.networkOptions = (response.entities).map(x => {
                const dto: NetworkDto = {
                  CorporateName: x.headOffice.corporateName,
                  DisplayName: x.headOffice.tradingName,
                  TradingName: x.headOffice.tradingName,
                  FiscalNumber: {
                    Value: x.fiscalNumber
                  }
                }
                return dto;
              });
              resolve();
            },
            error: err => {
              reject(err);
            }
          });
      });
      return this.networkOptions;
    }
    catch (x) {
      this.toast.showResponseErrorToast(x);
      return [];
    }
    finally {
      this.isLoadingNetwork = false;
    }

  }

  onSelectNetwork = (event: any) => {
    this.selectedNetwork = event as NetworkDto;
    this.rootForm.get('networkFilter').setValue(this.selectedNetwork ? this.selectedNetwork.FiscalNumber.Value : '');
    this.rootForm.updateValueAndValidity();
  }

  private searchNetworkTermByFiscalNumber(search: SearchNetworkPayload) {
    return this.networkService.odata
      .entities()
      .query((x) => {
        x.top(search.Top);
        x.skip(search.Skip);
        x.select(search.Select);
        x.expand(search.Expand);
        x.filter(search.Filter);
      })
      .fetch({
        withCount: true,
      });
  }
  //#endregion

  onSelectRow(element: any) {
    if (!element.color) {
      const colors = this.getColorPair(element.socialNumber || String(Math.random()));
      element.color = colors.dark;
      element.backgroundColor = colors.light;
    }
    if (element.selected) {
      if (element.title === 'Titular') {
        this.selectedBeneficiaries.unshift(element);
      } else {
        this.selectedBeneficiaries.push(element);
      }
    } else {
      this.selectedBeneficiaries = this.selectedBeneficiaries.filter(b => b !== element);
    }

    this.cdk.detectChanges();
    this.computeTotalsForSelectedBeneficiaries();
  }

  private getColorPair(seed: string): { light: string, dark: string } {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }
    let hue = Math.abs(hash) % 360;
    const saturation = 70;

    // Verifica se já existe algum selectedBeneficiary com a mesma cor
    const usedColors = this.selectedBeneficiaries.map(b => b.color);
    let colorPair = {
      light: `hsl(${hue}, ${saturation}%, 85%)`,
      dark: `hsl(${hue}, ${saturation}%, 35%)`
    };

    // Se já existe, incrementa o hue até achar uma cor não usada
    while (usedColors.includes(colorPair.dark)) {
      hue = (hue + 30) % 360;
      colorPair = {
        light: `hsl(${hue}, ${saturation}%, 85%)`,
        dark: `hsl(${hue}, ${saturation}%, 35%)`
      };
    }

    return colorPair;
  }

  private delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async fetchDataSource() {
    try {
      if (!this.beneficiary || !this.dependents) return;
      // marca que há requisições pendentes
      this.isLimitLoading = true;
      this.cdk.detectChanges();

      // monta dataSource (titular + dependentes) mantendo estrutura atual
      const holder = this.beneficiary ? [{
        title: "Titular",
        name: this.beneficiary.getName(),
        registration: this.beneficiary.getEmployee()?.getRegistration(),
        region: this.beneficiary.getAddress()?.getRegion(),
        socialNumber: this.beneficiary.getSocialNumber(),
        cardNumber: this.beneficiary.getCard()?.getCardNumber(),
        limit: null,
        dueDate: null,
        availableBalance: null,
        dependents: this.dependents.length,
        status: this.beneficiary.isActive(),
        color: null,
        backgroundColor: null,
        selected: false,
        limitType: this.beneficiary.getLimitType()
      }] : [];

      this.dataSource = [
        ...holder,
        ...this.dependents.map(dep => ({
          title: "Dependente",
          name: dep.getName(),
          registration: null,
          region: dep.getAddress()?.getRegion(),
          socialNumber: dep.getSocialNumber(),
          cardNumber: dep.getCard()?.getCardNumber(),
          limit: null,
          dueDate: null,
          availableBalance: null,
          dependents: null,
          status: dep.isActive(),
          color: null,
          backgroundColor: null,
          selected: false,
          limitType: dep.getLimitType()
        }))
      ];

      this.cdk.detectChanges();

      // Se já houver um selecionado, marca
      const find = this.dataSource.find(el => el.socialNumber === this.selectedBeneficiary?.getSocialNumber());
      if (find) {
        find.selected = true;
        this.onSelectRow(find); // onSelectRow já chama computeTotals
      }
      this.cdk.detectChanges();

      // Lista de jobs: incluir titular + dependentes
      const itemsToFetch = [
        ...this.dataSource // contém holder + dependentes
      ];

      // contador de requisições pendentes
      let pendingRequests = 0;

      // função helper para fetch e update de um item
      const fetchAndUpdate = async (item: any) => {
        // normaliza socialNumber (sem pontos/traços)
        const socialNorm = (item.socialNumber || "").replace(/[./-]/g, "");
        if (!socialNorm) return;

        // evita processar o mesmo socialNumber 2x
        // para dependentes usamos processedDependents; para titular dá pra usar mesma checagem
        if (this.processedDependents.has(socialNorm)) return;
        this.processedDependents.add(socialNorm);

        pendingRequests++;
        // marca global loading (já true), mas também mantemos a flag por linha

        this.cdk.detectChanges();

        try {
          // chama o serviço (mesmo endpoint que você usava)
          const balance = await this.financialStatementService.findFinancialBalanceBySocialBalance(
            socialNorm,
            this.fiscalNumber
          );
          this.beneficiaryLimitType = balance?.getLimitType();

          // atualiza o item na dataSource (procura por socialNumber exato)
          const findInData = this.dataSource.find(d => d.socialNumber === item.socialNumber);
          if (findInData) {
            findInData.limit = balance ? balance.getLimit() : 0;
            findInData.limitType = item.limitType
            findInData.dueDate = balance ? balance.getCycleClosedDay() : null;
            findInData.availableBalance = balance ? balance.getAvailable() : 0;
            this.isLimitLoading = false; // terminou para esta linha
          }

          // recalcula totais para os selecionados assim que esse item estiver disponível
          // (isso atende seu requisito: quando o cycle de um selecionado terminar, mostrar o saldo)
          this.computeTotalsForSelectedBeneficiaries();

          // atualiza view imediatamente
          this.cdk.detectChanges();
        } catch (err) {
          console.error('Erro ao carregar balance para', item.socialNumber, err);
          // em caso de erro, marca que parou de carregar para não ficar spinner infinito
          const findInData = this.dataSource.find(d => d.socialNumber === item.socialNumber);
          if (findInData) {
            this.isLimitLoading = false;
          }
          this.cdk.detectChanges();
        } finally {
          pendingRequests--;
          // se não houver mais requisições, desliga o spinner global
          if (pendingRequests <= 0) {
            this.isLimitLoading = false;
            this.cdk.detectChanges();
          }
        }
      };

      // dispara todos em paralelo (mas cada um faz update assim que termina)
      const promises = itemsToFetch.map(item => fetchAndUpdate(item));
      // aguardamos todas apenas para fins de fluxo (já que updates ocorrem individualmente)
      await Promise.all(promises).catch(e => {
        // swallow, porque já tratamos cada erro internamente
        console.error('Erro em Promise.all de balances', e);
      });

      // Mesmo se Promise.all já terminou, garantimos que global loading esteja false
      this.isLimitLoading = false;
      this.cdk.detectChanges();

    } catch (error) {
      console.error(error);
      this.isLimitLoading = false;
      try { this.cdk.detectChanges(); } catch (e) { /* ignore */ }
    }
  }


  private async financialBalance(socialNumber: string): Promise<FinancialBalance> {
    try {
      this.isLimitLoading = true;
      return await this.financialStatementService.findFinancialBalanceBySocialBalance(
        socialNumber,
        this.fiscalNumber
      );
    } catch (error) {
      return null;
    } finally {
      this.isLimitLoading = false;
    }

  }

  private definePeriodList(): void {
    if (this.companyLifeCycle) {
      this.dates = Period.getLastMonthsWithFixedDay(6, this.companyLifeCycle.getClosedDay());
    }
    else {
      this.dates = Period.getLastMonths(6);
    }
    this.rootForm.get("months").setValue([this.dates[0]]);
    this.rootForm.updateValueAndValidity();
  }

  hasCompany(): boolean {
    return !!this.company;
  }

  hasBeneficiaryList(): boolean {
    return this.beneficiaryList && this.beneficiaryList.getItems().length > 0;
  }

  hasHolder(): boolean {
    return !!this.beneficiary;
  }

  getColor(socialNumber: string) {
    const find = this.selectedBeneficiaries.find(el => el.socialNumber === socialNumber)
    if (find) {
      return find.color;
    }
    return null;
  }

  getBackgroundColor(socialNumber: string) {
    const find = this.selectedBeneficiaries.find(el => el.socialNumber === socialNumber)
    if (find) {
      return find.backgroundColor;
    }
    return null;
  }

  //region Financial Statement

  lengthFinancialStatement(element: any) {
    const find = this.financialStatementItems.filter(el => el.getBeneficiarySocialNumber() === element.socialNumber);
    return find.length;
  }

  totalSpentFinancialStatement(element: any): number {
    return element?.totalSpent ?? 0;
  }

  /**
 * Retorna o saldo disponível já calculado.
 * (opcional helper se quiser no template)
 */
  availableBalanceFor(element: any): number | null {
    return element?.availableBalance ?? null;
  }

  async onFilterChange(): Promise<void> {
    if (this.selectedBeneficiaries.length === 0) {
      this.toast.showWarningToast('Selecione ao menos um beneficiário para filtrar o extrato.');
      return;
    }
    const ref = this.loadingService.beginLoading();
    try {
      let search = null;
      const dependentsList = this.selectedBeneficiaries.filter(b => b.title === 'Dependente');
      let socialNumber = null;
      let includeAllDependents = false;
      let specificDependents = [];

      if (dependentsList.some(b => b.title === 'Dependente')) {
        if (this.selectedBeneficiaries.some(b => b.title === 'Titular')) {
          socialNumber = this.beneficiary.getSocialNumber();
          includeAllDependents = dependentsList.length === this.beneficiary.getDependets().length;
          specificDependents = includeAllDependents ? [] : dependentsList.map(b => b.socialNumber);
        } else {
          socialNumber = null;
          includeAllDependents = false;
          specificDependents = dependentsList.map(b => b.socialNumber);
        }
      } else {
        socialNumber = this.beneficiary.getSocialNumber();
        includeAllDependents = false;
        specificDependents = [];
      }

      search = {
        beneficiaryFilter: {
          socialNumber: this.beneficiary.getLimitType() === 1 ? this.beneficiary.getSocialNumber() : socialNumber,
          includeAllDependents: this.beneficiary.getLimitType() === 1 ? true : includeAllDependents,
          specificDependents: this.beneficiary.getLimitType() === 1 ? [] : specificDependents
        },
        months: this.rootForm.get('months').value.map(m => m.value) || [],
        networkFilter: this.rootForm.get('networkFilter').value || '',
        companyFiscalNumber: this.company.getFiscalNumber().replace(/[./-]/g, "")
      };
      const salesOrderResultList: SalesOrderResult[] = await this.financialStatementService.findFinancialStatement(search);
      this.totalBalance = FinancialStatementHeaderViewItemFactory.calculateTotalBalance(salesOrderResultList);
      this.financialStatementItems = FinancialStatementHeaderViewItemFactory.parseFinancialStatementViewItemList(salesOrderResultList, this.selectedBeneficiaries);
      this.computeTotalsForSelectedBeneficiaries()
    } catch (error) {
      console.error(error);
      this.toast.showWarningToast(error);
    } finally {
      this.loadingService.finishLoading(ref);
    }
  }

  private computeTotalsForSelectedBeneficiaries(): Promise<void> {
    if (!this.selectedBeneficiaries || this.selectedBeneficiaries.length === 0)
      return;

    const items = this.financialStatementItems || [];
    // Processa todos os beneficiários em paralelo e aguarda todos terminarem
    this.selectedBeneficiaries.map((beneficiary) => {

      this.cdk.detectChanges();
      try {
        switch (beneficiary.limitType) {
          case 1:
            beneficiary.totalSpent = this.totalBalance;
            beneficiary.availableBalance = Number(beneficiary.limit) - this.totalBalance;
            break;
          case 2:
            beneficiary.totalSpent = 0;
            beneficiary.availableBalance = 0;
            break;
          default:
            // 🔹 Individual
            const relatedItems = items.filter(
              (it) => it.getBeneficiarySocialNumber() === beneficiary.socialNumber
            );

            const totalSpent =
              FinancialStatementHeaderViewItemFactory.calculateIndividualTotalBalance(
                relatedItems
              ) || 0;

            beneficiary.totalSpent = totalSpent;
            beneficiary.availableBalance = Number(beneficiary.limit) - totalSpent;
            break;
        }
      } catch (err) {
        console.error("Erro ao calcular saldo do beneficiário:", err);
        beneficiary.totalSpent = 0;
        beneficiary.availableBalance = null;
      } finally {
        // Marca fim do carregamento
        this.cdk.detectChanges();
      }
    })
  }

  private cleanFinancialStatement(): void {
    this.beneficiary = null;
    this.dependents = [];
    this.totalBalance = null;
    this.financialStatementItems = [];
    this.rootForm.controls.networkFilter.setValue("");
    this.rootForm.controls.months.setValue(this.dates ? [this.dates[0]] : []);
    this.rootForm.updateValueAndValidity();
    this.dataSource = [];
    this.selectedBeneficiaries = [];
    this.selectedBeneficiary = null;
    this.processedDependents.clear();
  }

  //#endregion

}
